import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, InputGroup, Alert } from 'react-bootstrap';
import CaseTable from '../components/common/CaseTable';
import CaseDetailsTable from '../components/common/CaseDetailsTable';
import api from '../services/api';

const CaseHistoryPage = () => {
  // States for cases table
  const [cases, setCases] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState('case_id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filter, setFilter] = useState({});
  const [searchInput, setSearchInput] = useState('');
  
  // States for case details
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [caseDetails, setCaseDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // State for error messages
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch cases
  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getCases(
        filter,
        sort,
        sortOrder,
        currentPage,
        pageSize
      );
      
      setCases(response.data?.cases || []);
      setTotalPages(response.data?.nr_of_pages || 0);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases. Please try again.');
      setLoading(false);
    }
  }, [filter, sort, sortOrder, currentPage, pageSize]);

  // Fetch case details
  const fetchCaseDetails = useCallback(async (caseId) => {
    try {
      setLoadingDetails(true);
      setError(null);
      
      const response = await api.getCaseDetails(caseId);
      
      if (response.data?.error) {
        setError(response.data.error);
        setCaseDetails(null);
      } else {
        setCaseDetails(response.data);
      }
      
      setLoadingDetails(false);
    } catch (err) {
      console.error('Error fetching case details:', err);
      setError('Failed to load case details. Please try again.');
      setLoadingDetails(false);
    }
  }, []);

  // Handle case selection
  const handleCaseSelect = (caseId) => {
    setSelectedCaseId(caseId);
    fetchCaseDetails(caseId);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle sort change
  const handleSortChange = (field, order) => {
    setSort(field);
    setSortOrder(order);
  };

  // Handle page size change
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setFilter({ case_id: searchInput.trim() });
    } else {
      setFilter({});
    }
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setFilter({});
    setCurrentPage(1);
  };

  // Fetch cases on mount and when dependencies change
  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  return (
    <Container className="py-4">
      <h1 className="mb-4">Case History</h1>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Bar */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSearchSubmit}>
                <InputGroup>
                  <InputGroup.Text>🔍</InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by Case ID..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                  />
                  <button 
                    className="btn btn-primary" 
                    type="submit"
                  >
                    Search
                  </button>
                  {searchInput && (
                    <button 
                      className="btn btn-secondary" 
                      type="button"
                      onClick={handleClearSearch}
                    >
                      Clear
                    </button>
                  )}
                </InputGroup>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Cases Table */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Cases</h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center p-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading cases...</p>
                </div>
              ) : (
                <CaseTable
                  cases={cases}
                  totalPages={totalPages}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  sort={sort}
                  sortOrder={sortOrder}
                  onPageChange={handlePageChange}
                  onSortChange={handleSortChange}
                  onPageSizeChange={handlePageSizeChange}
                  onCaseSelect={handleCaseSelect}
                  selectedCaseId={selectedCaseId}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Case Details Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                {selectedCaseId 
                  ? `Case Details - Case ID: ${selectedCaseId}` 
                  : 'Case Details'}
              </h5>
            </Card.Header>
            <Card.Body>
              {loadingDetails ? (
                <div className="text-center p-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading case details...</p>
                </div>
              ) : (
                <CaseDetailsTable caseDetails={caseDetails} />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CaseHistoryPage;